import React, { useState } from 'react';
import { Loading, FestivalHeader } from '../controls';
import FilterableTable from '../table';
import Portal from '../modal/Portal';
import { FestivalVendorForm } from './';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import {
  useAllVendorsQuery,
  useFestivalVendorsQuery,
  useRemoveFestivalVendorMutation,
  useSaveFestivalVendorMutation,
} from '@/queries/vendors';

export function FestivalVendors() {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const festivalId = festival?.festivalId;

  const festivalVendorsQ = useFestivalVendorsQuery(festivalId);
  const allVendorsQ = useAllVendorsQuery();
  const saveMutation = useSaveFestivalVendorMutation(festivalId);
  const removeMutation = useRemoveFestivalVendorMutation(festivalId);

  const festivalVendors = festivalVendorsQ.data ?? [];
  const vendors = allVendorsQ.data ?? [];
  const loading =
    festivalId == null ||
    festivalVendorsQ.isPending ||
    festivalVendorsQ.isFetching ||
    allVendorsQ.isPending ||
    allVendorsQ.isFetching;

  const [selectedVendor, setSelectedVendor] = useState({});
  const [showModal, setShowModal] = useState(false);

  const buildColumnDefinitions = () => {
    var columnDefs = {};
    columnDefs.vendorName = {
      header: 'Vendor Name',
      filterable: true,
      sortable: true,
      showViewLink: false,
      handleClick: true,
    };
    columnDefs.category = {
      header: 'Category',
      filterable: false,
      sortable: false,
    };
    columnDefs.subcategory = {
      header: 'Subcategory',
      filterable: false,
      sortable: false,
    };
    return columnDefs;
  };

  const openModal = (vendor) => {
    const next =
      festivalVendors.find((v) => v.vendor.vendorId === vendor.vendorId) || {};
    setSelectedVendor(next);
    setShowModal(true);
  };

  const onLocationChange = (location) => {
    const updatedLocation = {
      ...selectedVendor.location,
      ...location,
      radius: 20,
    };
    setSelectedVendor((prev) => ({
      ...prev,
      location: updatedLocation,
    }));
  };

  const handleSave = async (vendor) => {
    const updatedVendor = { ...selectedVendor, ...vendor };
    try {
      await saveMutation.mutateAsync(updatedVendor);
      setShowModal(false);
      setSelectedVendor({});
    } catch {
      /* mutation toast */
    }
  };

  const handleRemove = async () => {
    try {
      await removeMutation.mutateAsync(selectedVendor.vendorId);
      setShowModal(false);
      setSelectedVendor({});
    } catch {
      /* mutation toast */
    }
  };

  const vendorListData = festivalVendors.map((v) => v.vendor);

  return (
    <>
      <FestivalHeader
        title="Festival Vendors"
        showExit={true}
        showAdd={true}
        addTitle="Add Vendor"
        addAction={() => {
          setSelectedVendor({});
          setShowModal(true);
        }}
      />
      {loading ? (
        <Loading />
      ) : (
        <FilterableTable
          data={vendorListData}
          tableTitle=""
          columns={buildColumnDefinitions()}
          includeFilter={false}
          editHandler={openModal}
          toggleModal={openModal}
        />
      )}

      <Portal
        header={
          selectedVendor.vendor
            ? selectedVendor.vendor.vendorName || 'Add Vendor'
            : 'Add Vendor'
        }
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <FestivalVendorForm
          vendor={selectedVendor}
          vendors={vendors}
          handleSave={handleSave}
          onLocationChange={onLocationChange}
          handleRemove={handleRemove}
        />
      </Portal>
    </>
  );
}
